import { LitElement, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { removeRmaLineUploadContext, removeTemporaryRmaLineUploadContext, uploadUrlContext } from 'components/ordering/rma/components/context/rma-context.js'
import { RMA_TEMPORARY_FILE_REMOVED } from 'components/ordering/rma/events.js'
import { fileViewer } from './file-viewer.style.js'

const translations = {
  remove: () => t('remove', { ns: 'csc-rma-file-viewer', default: 'Remove file' }),
}

const iconForName = (name: string): string => {
  const extension = name.split('.').pop()?.toLowerCase() ?? ''
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) return 'file-image'
  if (extension === 'pdf') return 'file-pdf'
  if (['doc', 'docx'].includes(extension)) return 'file-word'
  if (['xls', 'xlsx', 'csv'].includes(extension)) return 'file-excel'
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return 'file-zipper'
  return 'file'
}

@customElement('csc-rma-file-viewer')
export class CSCRmaFileViewer extends LitElement {
  static override styles = [hostStyle, fileViewer]

  @property({ attribute: 'line-id', type: String })
  public lineId = ''

  @property({ type: String })
  public identifier = ''

  @property({ type: String })
  public name = ''

  @property({ type: Boolean })
  public temporary = false

  @consume({ context: removeRmaLineUploadContext, subscribe: true })
  private removeUpload: ((lineId: string, identifier: string) => Promise<void>) | null = null

  @consume({ context: removeTemporaryRmaLineUploadContext, subscribe: true })
  private removeTemporaryUpload: ((identifier: string) => Promise<void>) | null = null

  @consume({ context: uploadUrlContext, subscribe: true })
  private uploadUrl = ''

  @state() private removing = false

  private get url(): string {
    const base = this.uploadUrl.endsWith('/') ? this.uploadUrl : `${this.uploadUrl}/`
    return `${base}${this.identifier}`
  }

  private async onRemove() {
    if (this.removing) return
    this.removing = true
    try {
      if (this.temporary) {
        if (!this.removeTemporaryUpload) return
        await this.removeTemporaryUpload(this.identifier)
        this.dispatchEvent(new CustomEvent(RMA_TEMPORARY_FILE_REMOVED, {
          detail: { identifier: this.identifier },
          bubbles: true,
          composed: true,
        }))
      } else {
        if (!this.removeUpload) return
        await this.removeUpload(this.lineId, this.identifier)
      }
    } finally {
      this.removing = false
    }
  }

  override render() {
    return html`
      <li class="rma-file-viewer" data-test="rma-file-viewer">
        <cse-icon icon-name="${iconForName(this.name)}" icon-style="light" size="sm"></cse-icon>
        <cse-link url="${this.url}" target="_blank">${this.name}</cse-link>
        <cse-icon-button
          variant="ghost"
          size="small"
          aria-label="${translations.remove()}"
          ?disabled="${this.removing}"
          @click="${() => this.onRemove()}"
        >
          <cse-icon icon-name="xmark" icon-style="light"></cse-icon>
        </cse-icon-button>
      </li>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-file-viewer': CSCRmaFileViewer
  }
}
